import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useId,
} from "react";
import { ChevronDown, Search, X, Phone, Check } from "lucide-react";

/**
 * Inline country list for the dashboard PhoneInput.
 *
 * NOTE: This is a fallback list (the dashboard does not depend on
 * libphonenumber-js). Validation is regex-based E.164 only — the
 * server still uses libphonenumber-js for authoritative normalization
 * via `backend/utils/phoneValidation.js`.
 *
 * Shape mirrors `frontend/lib/utils/countryList.ts` so the two stay
 * in lock-step visually.
 */
export interface CountryOption {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

export const COUNTRIES: CountryOption[] = [
  // MENA first
  { code: "AE", name: "United Arab Emirates", dialCode: "+971", flag: "🇦🇪" },
  { code: "SA", name: "Saudi Arabia", dialCode: "+966", flag: "🇸🇦" },
  { code: "KW", name: "Kuwait", dialCode: "+965", flag: "🇰🇼" },
  { code: "BH", name: "Bahrain", dialCode: "+973", flag: "🇧🇭" },
  { code: "OM", name: "Oman", dialCode: "+968", flag: "🇴🇲" },
  { code: "QA", name: "Qatar", dialCode: "+974", flag: "🇶🇦" },
  { code: "JO", name: "Jordan", dialCode: "+962", flag: "🇯🇴" },
  { code: "LB", name: "Lebanon", dialCode: "+961", flag: "🇱🇧" },
  { code: "IQ", name: "Iraq", dialCode: "+964", flag: "🇮🇶" },
  { code: "YE", name: "Yemen", dialCode: "+967", flag: "🇾🇪" },
  { code: "EG", name: "Egypt", dialCode: "+20", flag: "🇪🇬" },
  { code: "PS", name: "Palestine", dialCode: "+970", flag: "🇵🇸" },
  { code: "SY", name: "Syria", dialCode: "+963", flag: "🇸🇾" },
  { code: "MA", name: "Morocco", dialCode: "+212", flag: "🇲🇦" },
  { code: "TN", name: "Tunisia", dialCode: "+216", flag: "🇹🇳" },
  { code: "DZ", name: "Algeria", dialCode: "+213", flag: "🇩🇿" },
  { code: "LY", name: "Libya", dialCode: "+218", flag: "🇱🇾" },
  { code: "SD", name: "Sudan", dialCode: "+249", flag: "🇸🇩" },
  // Global
  { code: "US", name: "United States", dialCode: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧" },
  { code: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪" },
  { code: "FR", name: "France", dialCode: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italy", dialCode: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spain", dialCode: "+34", flag: "🇪🇸" },
  { code: "NL", name: "Netherlands", dialCode: "+31", flag: "🇳🇱" },
  { code: "TR", name: "Turkey", dialCode: "+90", flag: "🇹🇷" },
  { code: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" },
  { code: "PK", name: "Pakistan", dialCode: "+92", flag: "🇵🇰" },
  { code: "BD", name: "Bangladesh", dialCode: "+880", flag: "🇧🇩" },
  { code: "PH", name: "Philippines", dialCode: "+63", flag: "🇵🇭" },
  { code: "ID", name: "Indonesia", dialCode: "+62", flag: "🇮🇩" },
  { code: "MY", name: "Malaysia", dialCode: "+60", flag: "🇲🇾" },
  { code: "NG", name: "Nigeria", dialCode: "+234", flag: "🇳🇬" },
  { code: "ZA", name: "South Africa", dialCode: "+27", flag: "🇿🇦" },
  { code: "CN", name: "China", dialCode: "+86", flag: "🇨🇳" },
  { code: "JP", name: "Japan", dialCode: "+81", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", dialCode: "+82", flag: "🇰🇷" },
  { code: "BR", name: "Brazil", dialCode: "+55", flag: "🇧🇷" },
  { code: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺" },
];

export const DEFAULT_COUNTRY_CODE = "AE";

export const findCountryByCode = (
  code: string | null | undefined,
): CountryOption | undefined => {
  if (!code) return undefined;
  return COUNTRIES.find((c) => c.code === code);
};

// E.164 basic format check (used as the dashboard-side sanity gate).
// The server is authoritative and re-validates with libphonenumber-js.
const E164_REGEX = /^\+\d{7,15}$/;

/**
 * Returns true when the input is a syntactically-valid E.164 string.
 * Exported so parent forms can validate without depending on
 * PhoneInput's internal `country` callback (which only fires after the
 * user interacts with the field).
 */
export const isValidE164Phone = (s: string | undefined | null): boolean => {
  if (!s) return true;
  return E164_REGEX.test(s);
};

export interface PhoneInputProps {
  value?: string;
  onChange: (e164: string, country: string | null) => void;
  label?: string;
  placeholder?: string;
  optional?: boolean;
  disabled?: boolean;
  error?: string | null;
  helperText?: string;
  defaultCountry?: string;
  id?: string;
  className?: string;
  autoFocus?: boolean;
  inputClassName?: string;
}

const resolveInitialCountry = (
  initialValue: string | undefined,
  fallback: string,
): CountryOption => {
  if (initialValue) {
    const match = initialValue.match(/^\+(\d{1,4})/);
    if (match) {
      const dial = `+${match[1]}`;
      const found = COUNTRIES.find((c) => c.dialCode === dial);
      if (found) return found;
    }
  }
  // The COUNTRIES list is module-level so [0] is always defined, but
  // `noUncheckedIndexedAccess` widens the type. Fall back defensively.
  const firstCountry = COUNTRIES[0];
  return (
    findCountryByCode(fallback) ??
    findCountryByCode(DEFAULT_COUNTRY_CODE) ??
    firstCountry ?? {
      code: DEFAULT_COUNTRY_CODE,
      name: "United Arab Emirates",
      dialCode: "+971",
      flag: "🇦🇪",
    }
  );
};

const resolveInitialLocalNumber = (
  initialValue: string | undefined,
): string => {
  if (!initialValue) return "";
  // Match +<dial><rest>; the country lookup already handled the prefix.
  const country = resolveInitialCountry(
    initialValue,
    DEFAULT_COUNTRY_CODE,
  );
  if (initialValue.startsWith(country.dialCode)) {
    return initialValue.slice(country.dialCode.length);
  }
  // Fallback: strip non-digits and a leading '+'.
  return initialValue.replace(/[^\d]/g, "");
};

export function PhoneInput({
  value,
  onChange,
  label = "Phone Number",
  placeholder = "50 123 4567",
  optional = false,
  disabled = false,
  error = null,
  helperText,
  defaultCountry = DEFAULT_COUNTRY_CODE,
  id,
  className,
  autoFocus = false,
  inputClassName,
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(() =>
    resolveInitialCountry(value, defaultCountry),
  );
  const [localNumber, setLocalNumber] = useState<string>(() =>
    resolveInitialLocalNumber(value),
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const initializedRef = useRef(false);

  const generatedId = useId();
  const fieldId = id ?? generatedId;

  // Keep latest onChange in a ref so emitting doesn't need the parent to memoise.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Emit E.164 (or empty) whenever the user changes the local number or country.
  // Skip the first run so the parent's initial `value` is not echoed back.
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    const trimmed = localNumber.trim();
    if (trimmed === "") {
      onChangeRef.current("", null);
      return;
    }
    const stripped = localNumber.replace(/^0+/, "").replace(/[^\d]/g, "");
    if (stripped === "") {
      onChangeRef.current("", null);
      return;
    }
    const combined = selectedCountry.dialCode + stripped;
    if (E164_REGEX.test(combined)) {
      onChangeRef.current(combined, selectedCountry.code);
    } else {
      // Surface a partial-but-still-E.164-shaped value with no country claim
      // so the parent can show inline validation feedback if it wants.
      onChangeRef.current(combined, null);
    }
  }, [localNumber, selectedCountry.code, selectedCountry.dialCode]);

  // Filter countries based on the search query.
  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  // Reset highlight when the search query changes or the dropdown reopens,
  // using the "set state during render" pattern recommended by React
  // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  if (prevSearchQuery !== searchQuery) {
    setPrevSearchQuery(searchQuery);
    setHighlightedIndex(0);
  }

  const [prevDropdownOpen, setPrevDropdownOpen] = useState(isDropdownOpen);
  if (prevDropdownOpen !== isDropdownOpen) {
    setPrevDropdownOpen(isDropdownOpen);
    if (isDropdownOpen) {
      setHighlightedIndex(0);
      setSearchQuery("");
    }
  }

  // Close on outside click.
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Auto-focus search input when the dropdown opens.
  useEffect(() => {
    if (isDropdownOpen) {
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 30);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [isDropdownOpen]);

  const selectCountry = useCallback((country: CountryOption) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery("");
    // Refocus the local number input for a smooth keyboard flow.
    window.setTimeout(() => localInputRef.current?.focus(), 0);
  }, []);

  const moveHighlight = useCallback(
    (delta: number) => {
      if (filteredCountries.length === 0) return;
      setHighlightedIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return filteredCountries.length - 1;
        if (next >= filteredCountries.length) return 0;
        return next;
      });
    },
    [filteredCountries.length],
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = filteredCountries[highlightedIndex];
      if (target) selectCountry(target);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsDropdownOpen(false);
    }
  };

  const wrapperClass = [
    "border rounded-lg bg-white flex items-center overflow-hidden transition-shadow",
    error
      ? "border-red-500 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500"
      : "border-gray-300 focus-within:ring-2 focus-within:ring-[#102040] focus-within:border-[#102040]",
    disabled ? "opacity-60" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`w-full ${className ?? ""}`.trim()}>
      {label && (
        <label
          htmlFor={fieldId}
          className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2"
        >
          <Phone className="h-3.5 w-3.5 text-gray-500" />
          {label}
          {optional && (
            <span className="text-xs text-gray-400 font-normal">
              (optional)
            </span>
          )}
        </label>
      )}

      <div className="relative" ref={wrapperRef}>
        <div className={wrapperClass}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            className="flex items-center gap-1.5 px-3 py-2 border-r border-gray-200 hover:bg-gray-50 cursor-pointer min-w-fit disabled:cursor-not-allowed focus:outline-none"
          >
            <span className="text-base leading-none">
              {selectedCountry.flag}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {selectedCountry.dialCode}
            </span>
            <ChevronDown
              className={`h-3.5 w-3.5 text-gray-500 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <input
            ref={localInputRef}
            id={fieldId}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={localNumber}
            onChange={(e) => setLocalNumber(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            aria-invalid={Boolean(error)}
            className={`flex-1 px-3 py-2 text-sm focus:outline-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed ${inputClassName ?? ""}`.trim()}
          />

          {localNumber && !disabled && (
            <button
              type="button"
              onClick={() => {
                setLocalNumber("");
                localInputRef.current?.focus();
              }}
              aria-label="Clear phone number"
              className="flex items-center justify-center px-2 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {isDropdownOpen && (
          <div
            className="absolute z-50 mt-1 w-72 max-w-[calc(100vw-2rem)] left-0 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden transition-all duration-150 opacity-100 translate-y-0"
            role="listbox"
            aria-label="Select country"
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search country, code, or dial..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#102040] focus:border-[#102040]"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto py-1">
              {filteredCountries.length === 0 ? (
                <div className="px-3 py-6 text-sm text-gray-500 text-center">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((country, idx) => {
                  const isSelected = country.code === selectedCountry.code;
                  const isHighlighted = idx === highlightedIndex;
                  return (
                    <button
                      key={country.code}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => selectCountry(country)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      className={`flex items-center justify-between w-full gap-2 px-3 py-2 text-sm text-left transition-colors focus:outline-none ${
                        isHighlighted
                          ? "bg-gray-100 text-gray-900"
                          : "bg-transparent text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-base leading-none shrink-0">
                          {country.flag}
                        </span>
                        <span className="truncate">{country.name}</span>
                      </span>
                      <span className="flex items-center gap-2 text-gray-500 shrink-0">
                        <span className="text-xs">{country.dialCode}</span>
                        {isSelected && (
                          <Check className="h-4 w-4 text-[#102040]" />
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {error ? (
        <p className="mt-1.5 text-xs text-red-500">{error}</p>
      ) : helperText ? (
        <p className="mt-1.5 text-xs text-gray-500">{helperText}</p>
      ) : null}
    </div>
  );
}

export default PhoneInput;